/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useCertificateForm.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
     Certificate form hook — includes DNS-01 challenge polling flow.
────────────────────────────────────────────────────────────────*/


import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
    fetchCertificate,
    createCertificate,
    updateCertificate,
    deleteCertificate,
    parseCertificate,
    previewSelfSigned,
    confirmDnsChallenge,
} from '../api';
import {
    Certificate,
    CertificateFormData,
    CertificateInfo,
    DEFAULT_FORM_DATA,
    CertificateType,
} from '../types';

const LIST_ROUTE = '/admin/system-settings/certificates';

export interface DnsChallenge {
    domain: string;
    value: string;
    certId: number;
}

export function useCertificateForm(uuid?: string) {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const isEditMode = !!(uuid && uuid !== 'new');

    const [formData, setFormData]         = useState<CertificateFormData>(DEFAULT_FORM_DATA);
    const [originalData, setOriginalData] = useState<CertificateFormData>(DEFAULT_FORM_DATA);
    const [certRecord, setCertRecord]     = useState<Certificate | null>(null);
    const [certificateId, setCertificateId] = useState<number | null>(null);
    const [loading, setLoading]           = useState(false);
    const [isSaving, setIsSaving]         = useState(false);
    const [isDeleting, setIsDeleting]     = useState(false);
    const [isConfirmingDnsState, setIsConfirmingDnsState] = useState(false);
    const isConfirmingRef       = useRef(false);
    const isConfirmingDns       = isConfirmingDnsState;
    const setIsConfirmingDns    = (val: boolean) => { isConfirmingRef.current = val; setIsConfirmingDnsState(val); };
    const [validating, setValidating]     = useState(false);
    const [errors, setErrors]             = useState<Record<string, string>>({});
    const [certInfo, setCertInfo]         = useState<CertificateInfo | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPrivateKey, setShowPrivateKey]   = useState(false);
    const pollRef              = useRef<ReturnType<typeof setInterval> | null>(null);
    // Tracks the last known challenge token to detect regeneration after a certbot restart
    const prevChallengeRef      = useRef<string | null>(null);
    const challengeInitialised  = useRef(false);

    // ── Derived: DNS challenge info ─────────────────────────
    const dnsChallenge: DnsChallenge | null = (() => {
        const err = certRecord?.lastActionError || '';
        if (!err.startsWith('DNS_CHALLENGE:')) return null;
        const parts = err.split(':');
        // format: DNS_CHALLENGE:_acme-challenge.domain:VALUE
        return {
            domain: parts[1] || '',
            value: parts[2] || '',
            certId: certRecord?.id || 0,
        };
    })();

    // True while certbot is generating the challenge token (PENDING but no DNS_CHALLENGE in DB yet)
    const isWaitingForChallenge =
        isEditMode &&
        certRecord !== null &&
        certRecord.status === 'PENDING' &&
        !dnsChallenge;

    // ── Load ─────────────────────────────────────────────────
    useEffect(() => {
        if (isEditMode && uuid) loadCertificate(uuid);
    }, [isEditMode, uuid]);

    // ── Polling: watch PENDING status for DNS-01 challenge ──
    useEffect(() => {
        const shouldPoll = isEditMode && uuid && certRecord?.status === 'PENDING';
        if (!shouldPoll) return;

        let isPolling = true;

        // Seed the prevChallengeRef on the first poll cycle
        if (!challengeInitialised.current) {
            const currentErr = certRecord?.lastActionError || '';
            prevChallengeRef.current = currentErr.startsWith('DNS_CHALLENGE:')
                ? currentErr.split(':')[2] || null
                : null;
            challengeInitialised.current = true;
        }

        const poll = async () => {
            if (!isPolling) return;
            
            // Skip API call if we are currently holding the long-running POST confirm request
            if (isConfirmingRef.current) {
                if (isPolling) pollRef.current = setTimeout(poll, 5000);
                return;
            }

            try {
                const fresh = await fetchCertificate(uuid!);

                // ── Detect newly generated challenge token (restart flow) ──
                const freshErr = fresh.lastActionError || '';
                const freshTokenValue = freshErr.startsWith('DNS_CHALLENGE:')
                    ? freshErr.split(':')[2] || null
                    : null;

                if (
                    freshTokenValue &&
                    freshTokenValue !== prevChallengeRef.current
                ) {
                    // A new (or regenerated) token just appeared — notify user
                    if (prevChallengeRef.current !== null || (fresh.lastAction === 'dns_challenge_pending' && certRecord?.lastAction === 'restarting_challenge')) {
                        showSuccess(t('certificates.messages.dnsTokenGenerated'));
                    }
                    prevChallengeRef.current = freshTokenValue;
                }

                setCertRecord(fresh);

                if (fresh.status !== 'PENDING') {
                    isPolling = false;
                    challengeInitialised.current = false;
                    prevChallengeRef.current = null;
                    if (fresh.status === 'ACTIVE') {
                        showSuccess(t('certificates.messages.issuedSuccess'));
                    } else if (fresh.status === 'ERROR') {
                        const errMsg = fresh.lastActionError || t('certificates.messages.issueError');
                        showError(errMsg.length > 300 ? errMsg.slice(0, 297) + '…' : errMsg);
                    }
                    loadCertificate(uuid!);
                    return;
                }
            } catch { /* silent */ }
            
            if (isPolling) {
                pollRef.current = setTimeout(poll, 5000); // Poll every 5s
            }
        };

        // Start first poll after 5s
        pollRef.current = setTimeout(poll, 5000);

        return () => {
            isPolling = false;
            if (pollRef.current) clearTimeout(pollRef.current);
        };
    }, [isEditMode, uuid, certRecord?.status]);

    async function loadCertificate(certUuid: string) {
        try {
            setLoading(true);
            const cert = await fetchCertificate(certUuid);
            setCertRecord(cert);
            const mapped: CertificateFormData = {
                name: cert.name,
                type: cert.type,
                commonName: cert.commonName,
                subjectAltNames: cert.subjectAltNames || [],
                keySize: cert.keySize,
                validityDays: cert.validityDays,
                acmeAccountEmail: cert.acmeAccountEmail || '',
                acmeChallengeType: (cert.acmeChallengeType as 'http-01' | 'dns-01') || 'http-01',
                autoRenew: cert.autoRenew,
                renewBeforeDays: cert.renewBeforeDays,
                certificate: '',
                privateKey: '',
                certificateChain: '',
            };
            setFormData(mapped);
            setOriginalData(mapped);
            setCertificateId(cert.id);

            if (cert.issuer || cert.notAfter) {
                setCertInfo({
                    commonName: cert.commonName,
                    issuer: cert.issuer || '',
                    serialNumber: cert.serialNumber || '',
                    fingerprint: cert.fingerprint || '',
                    notBefore: cert.notBefore || '',
                    notAfter: cert.notAfter || '',
                    subjectAltNames: cert.subjectAltNames || [],
                    isValid: cert.status === 'ACTIVE',
                    daysUntilExpiry: cert.notAfter
                        ? Math.ceil((new Date(cert.notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                        : 0,
                });
            }
        } catch {
            showError(t('certificates.messages.loadError'));
            navigate(LIST_ROUTE);
        } finally {
            setLoading(false);
        }
    }

    // ── Field change ─────────────────────────────────────────
    function handleChange(field: keyof CertificateFormData, value: CertificateFormData[keyof CertificateFormData]) {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
        }
    }

    // ── Auto-switch to dns-01 for wildcard domains ───────────
    function handleCommonNameChange(value: string) {
        handleChange('commonName', value);
        if (value.startsWith('*.') && formData.acmeChallengeType !== 'dns-01' && formData.type === 'LETS_ENCRYPT') {
            handleChange('acmeChallengeType', 'dns-01');
        }
    }

    // ── Validation ───────────────────────────────────────────
    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) newErrors.name = t('certificates.validation.nameRequired');
        if (!formData.commonName.trim()) newErrors.commonName = t('certificates.validation.commonNameRequired');
        if (formData.type === 'CUSTOM' && !isEditMode) {
            if (!formData.certificate.trim()) newErrors.certificate = t('certificates.validation.certificateRequired');
            if (!formData.privateKey.trim()) newErrors.privateKey = t('certificates.validation.privateKeyRequired');
        }
        if (formData.type === 'LETS_ENCRYPT') {
            if (!formData.acmeAccountEmail.trim()) {
                newErrors.acmeAccountEmail = t('certificates.validation.emailRequired');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.acmeAccountEmail)) {
                newErrors.acmeAccountEmail = t('certificates.validation.emailInvalid');
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    // ── Validate certificate PEM ─────────────────────────────
    async function handleValidateCertificate() {
        if (!formData.certificate) return;
        try {
            setValidating(true);
            const info = await parseCertificate(formData.certificate, formData.privateKey || undefined);
            setCertInfo(info);
            if (info.keyMatch === false) {
                setErrors(prev => ({ ...prev, privateKey: t('certificates.validation.keyMismatch') }));
            }
        } catch {
            showError(t('certificates.messages.parseError'));
        } finally {
            setValidating(false);
        }
    }

    // ── Preview self-signed ──────────────────────────────────
    async function handlePreviewSelfSigned() {
        if (!formData.commonName) return;
        try {
            setValidating(true);
            const result = await previewSelfSigned({
                commonName: formData.commonName,
                subjectAltNames: formData.subjectAltNames.filter(s => s.trim()),
                keySize: formData.keySize,
                validityDays: formData.validityDays,
            });
            setCertInfo(result.certificateInfo);
            showSuccess(t('certificates.messages.previewSuccess'));
        } catch {
            showError(t('certificates.messages.previewError'));
        } finally {
            setValidating(false);
        }
    }

    // ── Save ─────────────────────────────────────────────────
    async function handleSave() {
        if (!validateForm()) return;
        try {
            setIsSaving(true);
            const payload: Partial<CertificateFormData> = {
                name: formData.name,
                type: formData.type,
                commonName: formData.commonName,
                subjectAltNames: formData.subjectAltNames.filter(s => s.trim()),
            };
            if (formData.type === 'SELF_SIGNED') {
                payload.keySize = formData.keySize;
                payload.validityDays = formData.validityDays;
            } else if (formData.type === 'LETS_ENCRYPT') {
                payload.acmeAccountEmail = formData.acmeAccountEmail;
                payload.acmeChallengeType = formData.acmeChallengeType;
                payload.autoRenew = formData.autoRenew;
                payload.renewBeforeDays = formData.renewBeforeDays;
            } else if (formData.type === 'CUSTOM') {
                if (formData.certificate) payload.certificate = formData.certificate;
                if (formData.privateKey) payload.privateKey = formData.privateKey;
                if (formData.certificateChain) payload.certificateChain = formData.certificateChain;
            }

            if (isEditMode && certificateId) {
                await updateCertificate(certificateId, payload);
                showSuccess(t('certificates.messages.updateSuccess'));
                navigate(LIST_ROUTE);
            } else {
                const newCert = await createCertificate(payload);
                // For DNS-01: navigate to edit page and start polling
                if (formData.type === 'LETS_ENCRYPT' && formData.acmeChallengeType === 'dns-01') {
                    showSuccess(t('certificates.messages.dnsChallengePending'));
                    navigate(`${LIST_ROUTE}/${newCert.uuid}`);
                } else {
                    showSuccess(t('certificates.messages.createSuccess'));
                    navigate(LIST_ROUTE);
                }
            }
        } catch (error: unknown) {
            showError((error as Error)?.message || t('certificates.messages.saveError'));
        } finally {
            setIsSaving(false);
        }
    }

    // ── Confirm DNS challenge ────────────────────────────────
    async function handleConfirmDns() {
        if (!certRecord?.id) return;
        setIsConfirmingDns(true);
        try {
            await confirmDnsChallenge(certRecord.id);
            // Neutral message — the backend may be restarting certbot (new token incoming)
            // or signalling an alive process; polling will surface the real outcome.
            showSuccess(t('certificates.messages.processingAutoUpdate'));
            if (uuid) await loadCertificate(uuid);
        } catch (error: any) {
            const msg = error?.response?.data?.error || error?.message || t('certificates.messages.dnsConfirmError');
            showError(msg);
            if (uuid) await loadCertificate(uuid);
        } finally {
            setIsConfirmingDns(false);
        }
    }

    // ── Delete ───────────────────────────────────────────────
    async function handleDeleteConfirm() {
        if (!isEditMode || !certificateId) return;
        setIsDeleting(true);
        try {
            await deleteCertificate(certificateId);
            showSuccess(t('certificates.messages.deleteSuccess'));
            navigate(LIST_ROUTE);
        } catch (error: unknown) {
            showError((error as Error)?.message || t('certificates.messages.deleteError'));
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    }

    return {
        // State
        formData, errors, certInfo, certRecord, dnsChallenge,
        isWaitingForChallenge,
        loading, isSaving, isDeleting, isConfirmingDns, validating,
        showDeleteModal, showPrivateKey, isEditMode,
        // Actions
        handleChange,
        handleCommonNameChange,
        handleValidateCertificate,
        handlePreviewSelfSigned,
        handleSave,
        handleConfirmDns,
        handleCancel: () => { setFormData(originalData); setErrors({}); },
        handleDeleteClick: () => { if (isEditMode && certificateId) setShowDeleteModal(true); },
        handleDeleteConfirm,
        setShowDeleteModal,
        setShowPrivateKey,
        goToList: () => navigate(LIST_ROUTE),
        goToNew: () => navigate(`${LIST_ROUTE}/new`),
    };
}
